import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Table, Download, Filter, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import * as XLSX from 'xlsx';

const sanitizeFileName = (name: string) => {
  const base = name
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .replace(/-+/g, '-')
    .toLowerCase();
  return base || 'table';
};

const sanitizeSheetName = (name: string) => {
  const cleaned = name.replace(/[\[\]\*\/\\\?\:]/g, '').trim();
  return (cleaned || 'Sheet1').slice(0, 31);
};

const normalizeCellForExport = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const normalizeCellForSheet = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return value as string | number | boolean;
};

const escapeCsvValue = (value: unknown) => {
  const text = normalizeCellForExport(value);
  const escaped = text.replace(/"/g, '""');
  return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
};

const buildCsvContent = (headers: string[], rows: Record<string, any>[]) => {
  const lines = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(',')),
  ];
  return `\uFEFF${lines.join('\r\n')}`;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

type ColumnType = 'string' | 'number' | 'date' | 'boolean' | 'unknown';

type FilterOperator =
  | 'contains'
  | 'not_contains'
  | 'equals'
  | 'not_equals'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'on'
  | 'before'
  | 'after'
  | 'on_or_before'
  | 'on_or_after'
  | 'is_true'
  | 'is_false'
  | 'is_empty'
  | 'is_not_empty';

interface FilterRow {
  id: string;
  column: string;
  operator: FilterOperator;
  value: string;
  valueTo: string;
}

interface OperatorOption {
  value: FilterOperator;
  label: string;
  needsValue?: boolean;
  needsSecondValue?: boolean;
}

const operatorOptions: Record<ColumnType, OperatorOption[]> = {
  string: [
    { value: 'contains', label: 'Contains', needsValue: true },
    { value: 'not_contains', label: 'Does not contain', needsValue: true },
    { value: 'equals', label: 'Equals', needsValue: true },
    { value: 'not_equals', label: 'Not equals', needsValue: true },
    { value: 'starts_with', label: 'Starts with', needsValue: true },
    { value: 'ends_with', label: 'Ends with', needsValue: true },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  number: [
    { value: 'equals', label: 'Equals', needsValue: true },
    { value: 'not_equals', label: 'Not equals', needsValue: true },
    { value: 'gt', label: 'Greater than', needsValue: true },
    { value: 'gte', label: 'Greater or equal', needsValue: true },
    { value: 'lt', label: 'Less than', needsValue: true },
    { value: 'lte', label: 'Less or equal', needsValue: true },
    { value: 'between', label: 'Between', needsValue: true, needsSecondValue: true },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  date: [
    { value: 'on', label: 'On', needsValue: true },
    { value: 'before', label: 'Before', needsValue: true },
    { value: 'after', label: 'After', needsValue: true },
    { value: 'on_or_before', label: 'On or before', needsValue: true },
    { value: 'on_or_after', label: 'On or after', needsValue: true },
    { value: 'between', label: 'Between', needsValue: true, needsSecondValue: true },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  boolean: [
    { value: 'is_true', label: 'Is true' },
    { value: 'is_false', label: 'Is false' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
  unknown: [
    { value: 'contains', label: 'Contains', needsValue: true },
    { value: 'not_contains', label: 'Does not contain', needsValue: true },
    { value: 'equals', label: 'Equals', needsValue: true },
    { value: 'not_equals', label: 'Not equals', needsValue: true },
    { value: 'starts_with', label: 'Starts with', needsValue: true },
    { value: 'ends_with', label: 'Ends with', needsValue: true },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ],
};

const getOperatorMeta = (type: ColumnType, operator: FilterOperator) =>
  operatorOptions[type]?.find((option) => option.value === operator) ??
  operatorOptions.string.find((option) => option.value === operator);

const getDefaultOperator = (type: ColumnType) =>
  operatorOptions[type]?.[0]?.value ?? operatorOptions.string[0].value;

const createFilterId = () => `filter-${Math.random().toString(36).slice(2, 9)}`;

const isEmptyValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const isNumericString = (value: string) =>
  /^-?\d+(\.\d+)?$/.test(value.replace(/,/g, ''));

const isDateOnly = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const inferValueType = (value: unknown): ColumnType => {
  if (typeof value === 'number' && Number.isFinite(value)) return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (value instanceof Date) return 'date';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 'string';
    if (isNumericString(trimmed)) return 'number';
    if (!Number.isNaN(Date.parse(trimmed)) && /[T\-\/]/.test(trimmed)) return 'date';
    return 'string';
  }
  return 'unknown';
};

const inferColumnTypes = (rows: Record<string, any>[], headers: string[]) => {
  const types: Record<string, ColumnType> = {};
  headers.forEach((header) => {
    for (const row of rows) {
      const value = row[header];
      if (value !== null && value !== undefined && value !== '') {
        types[header] = inferValueType(value);
        return;
      }
    }
    types[header] = 'string';
  });
  return types;
};

const parseNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return Number.NaN;
    const normalized = trimmed.replace(/,/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }
  return Number.NaN;
};

const parseBoolean = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
    if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  }
  return undefined;
};

const toDateKey = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (isDateOnly(trimmed)) return trimmed;
  }
  const parsed = value instanceof Date ? value.getTime() : Date.parse(String(value));
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toISOString().slice(0, 10);
};

const parseDateInput = (value: string, endOfDay: boolean) => {
  const trimmed = value.trim();
  if (!trimmed) return Number.NaN;
  if (isDateOnly(trimmed)) {
    const base = Date.parse(trimmed);
    if (Number.isNaN(base)) return Number.NaN;
    return endOfDay ? base + 86_399_999 : base;
  }
  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
};

const doesFilterMatch = (
  row: Record<string, any>,
  filter: FilterRow,
  columnType: ColumnType
) => {
  if (!filter.column) return true;
  const cellValue = row[filter.column];
  const operator = filter.operator;
  const isEmpty = isEmptyValue(cellValue);

  if (operator === 'is_empty') return isEmpty;
  if (operator === 'is_not_empty') return !isEmpty;

  const meta = getOperatorMeta(columnType, operator);
  if (meta?.needsValue && !filter.value.trim()) return true;
  if (meta?.needsSecondValue && !filter.valueTo.trim()) return true;

  switch (columnType) {
    case 'number': {
      const cellNumber = parseNumber(cellValue);
      if (Number.isNaN(cellNumber)) return false;
      const primary = parseNumber(filter.value);
      if (Number.isNaN(primary)) return false;
      switch (operator) {
        case 'equals':
          return cellNumber === primary;
        case 'not_equals':
          return cellNumber !== primary;
        case 'gt':
          return cellNumber > primary;
        case 'gte':
          return cellNumber >= primary;
        case 'lt':
          return cellNumber < primary;
        case 'lte':
          return cellNumber <= primary;
        case 'between': {
          const secondary = parseNumber(filter.valueTo);
          if (Number.isNaN(secondary)) return false;
          const min = Math.min(primary, secondary);
          const max = Math.max(primary, secondary);
          return cellNumber >= min && cellNumber <= max;
        }
        default:
          return true;
      }
    }
    case 'date': {
      const cellTime = parseDateInput(String(cellValue ?? ''), false);
      if (Number.isNaN(cellTime)) return false;
      switch (operator) {
        case 'on': {
          const cellKey = toDateKey(cellValue);
          const filterKey = toDateKey(filter.value);
          return Boolean(cellKey && filterKey && cellKey === filterKey);
        }
        case 'before': {
          const threshold = parseDateInput(filter.value, false);
          return !Number.isNaN(threshold) && cellTime < threshold;
        }
        case 'after': {
          const threshold = parseDateInput(filter.value, true);
          return !Number.isNaN(threshold) && cellTime > threshold;
        }
        case 'on_or_before': {
          const threshold = parseDateInput(filter.value, true);
          return !Number.isNaN(threshold) && cellTime <= threshold;
        }
        case 'on_or_after': {
          const threshold = parseDateInput(filter.value, false);
          return !Number.isNaN(threshold) && cellTime >= threshold;
        }
        case 'between': {
          const start = parseDateInput(filter.value, false);
          const end = parseDateInput(filter.valueTo, true);
          return (
            !Number.isNaN(start) &&
            !Number.isNaN(end) &&
            cellTime >= Math.min(start, end) &&
            cellTime <= Math.max(start, end)
          );
        }
        default:
          return true;
      }
    }
    case 'boolean': {
      const cellBool = parseBoolean(cellValue);
      if (cellBool === undefined) return false;
      if (operator === 'is_true') return cellBool === true;
      if (operator === 'is_false') return cellBool === false;
      return true;
    }
    case 'string':
    default: {
      const cellText = normalizeCellForExport(cellValue).toLowerCase();
      const query = filter.value.trim().toLowerCase();
      if (!query) return true;
      switch (operator) {
        case 'contains':
          return cellText.includes(query);
        case 'not_contains':
          return !cellText.includes(query);
        case 'equals':
          return cellText === query;
        case 'not_equals':
          return cellText !== query;
        case 'starts_with':
          return cellText.startsWith(query);
        case 'ends_with':
          return cellText.endsWith(query);
        default:
          return true;
      }
    }
  }
};

const applyFilters = (
  rows: Record<string, any>[],
  filters: FilterRow[],
  columnTypes: Record<string, ColumnType>
) => {
  if (!filters.length) return rows;
  return rows.filter((row) =>
    filters.every((filter) =>
      doesFilterMatch(row, filter, columnTypes[filter.column] ?? 'string')
    )
  );
};

const isFilterActive = (filter: FilterRow, columnType: ColumnType) => {
  if (!filter.column) return false;
  const meta = getOperatorMeta(columnType, filter.operator);
  if (!meta) return false;
  if (meta.needsValue && !filter.value.trim()) return false;
  if (meta.needsSecondValue && !filter.valueTo.trim()) return false;
  return true;
};

interface TableRendererProps {
  data: Record<string, any>[];
  title?: string;
  className?: string;
}

export const TableRenderer: React.FC<TableRendererProps> = ({
  data,
  title,
  className,
}) => {
  const { language } = useLanguage();
  const labels = React.useMemo(
    () =>
      language === 'am'
        ? {
            noDataAvailable: 'የሚታይ ውሂብ የለም',
            noDataHint: 'እባክዎ ጥያቄ መለኪያዎችን ያስተካክሉ',
            row: 'ረድፍ',
            rows: 'ረድፎች',
            column: 'አምድ',
            columns: 'አምዶች',
            filters: 'ማጣሪያዎች',
            filtersShort: 'ማጣሪያ',
            downloadCsv: 'CSV አውርድ',
            downloadXlsx: 'XLSX አውርድ',
            addFilter: 'ማጣሪያ ጨምር',
            clear: 'አጽዳ',
            noFiltersYet: 'እስካሁን ማጣሪያ አልተጨመረም። ውጤቱን ለማጥበብ አንዱን ያክሉ።',
            value: 'እሴት',
            to: 'እስከ',
            noValueRequired: 'እሴት አያስፈልግም',
            removeFilter: 'ማጣሪያ አስወግድ',
            noResultsMatch: 'በአሁኑ ማጣሪያ የሚዛመድ ውጤት የለም።',
            showingOf: 'በማሳየት ላይ',
            results: 'ውጤቶች',
            result: 'ውጤት',
            updatedJustNow: 'አሁን ተዘምኗል',
          }
        : {
            noDataAvailable: 'No data available',
            noDataHint: 'Try adjusting your query parameters',
            row: 'row',
            rows: 'rows',
            column: 'column',
            columns: 'columns',
            filters: 'Filters',
            filtersShort: 'Filters',
            downloadCsv: 'Download CSV',
            downloadXlsx: 'Download XLSX',
            addFilter: 'Add filter',
            clear: 'Clear',
            noFiltersYet: 'No filters added yet. Add one to narrow results.',
            value: 'Value',
            to: 'to',
            noValueRequired: 'No value required',
            removeFilter: 'Remove filter',
            noResultsMatch: 'No results match the current filters.',
            showingOf: 'Showing',
            results: 'results',
            result: 'result',
            updatedJustNow: 'Updated just now',
          },
    [language]
  );

  const safeData = Array.isArray(data) ? data : [];
  const headers = React.useMemo(
    () => (safeData[0] ? Object.keys(safeData[0]) : []),
    [safeData]
  );
  const columnTypes = React.useMemo<Record<string, ColumnType>>(
    () => (headers.length ? inferColumnTypes(safeData, headers) : {}),
    [safeData, headers]
  );
  const [filters, setFilters] = React.useState<FilterRow[]>([]);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  React.useEffect(() => {
    if (!headers.length) return;
    setFilters((prev) =>
      prev
        .filter((filter) => headers.includes(filter.column))
        .map((filter) => {
          const nextType = columnTypes[filter.column] ?? 'string';
          const validOperators = operatorOptions[nextType]?.map((opt) => opt.value) ?? [];
          if (!validOperators.includes(filter.operator)) {
            return {
              ...filter,
              operator: getDefaultOperator(nextType),
              value: '',
              valueTo: '',
            };
          }
          return filter;
        })
    );
  }, [headers, columnTypes]);

  const activeFilterCount = React.useMemo(
    () =>
      filters.filter((filter) =>
        isFilterActive(filter, columnTypes[filter.column] ?? 'string')
      ).length,
    [filters, columnTypes]
  );
  const filteredData = React.useMemo(
    () => applyFilters(safeData, filters, columnTypes),
    [safeData, filters, columnTypes]
  );

  const fileBaseName = sanitizeFileName(title ?? 'table');
  const sheetName = sanitizeSheetName(title ?? 'Sheet1');

  const handleExportCsv = () => {
    const csvContent = buildCsvContent(headers, filteredData);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, `${fileBaseName}.csv`);
  };

  const handleExportXlsx = () => {
    const worksheetData = [
      headers,
      ...filteredData.map((row) =>
        headers.map((header) => normalizeCellForSheet(row[header]))
      ),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    triggerDownload(blob, `${fileBaseName}.xlsx`);
  };

  const addFilter = () => {
    const defaultColumn = headers[0];
    if (!defaultColumn) return;
    const type = columnTypes[defaultColumn] ?? 'string';
    setFilters((prev) => [
      ...prev,
      {
        id: createFilterId(),
        column: defaultColumn,
        operator: getDefaultOperator(type),
        value: '',
        valueTo: '',
      },
    ]);
    setIsFilterOpen(true);
  };

  const updateFilter = (id: string, updates: Partial<FilterRow>) => {
    setFilters((prev) =>
      prev.map((filter) => (filter.id === id ? { ...filter, ...updates } : filter))
    );
  };

  const removeFilter = (id: string) => {
    setFilters((prev) => prev.filter((filter) => filter.id !== id));
  };

  const clearFilters = () => {
    setFilters([]);
  };

  if (!safeData || safeData.length === 0) {
    return (
      <Card className={className}>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mb-3">
              <Table className="w-6 h-6 text-secondary-400" />
            </div>
            <p className="text-sm text-secondary-500 font-medium">{labels.noDataAvailable}</p>
            <p className="text-xs text-secondary-400 mt-1">{labels.noDataHint}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-lg flex items-center justify-center">
                <Table className="w-4 h-4 text-secondary-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-secondary-900">{title}</h3>
                <p className="text-xs text-secondary-600 font-medium">
                  {filteredData.length}{' '}
                  {filteredData.length !== 1 ? labels.rows : labels.row} x {headers.length}{' '}
                  {headers.length !== 1 ? labels.columns : labels.column}
                  {activeFilterCount > 0 && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                      {labels.filters} {activeFilterCount}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsFilterOpen((prev) => !prev)}
                aria-pressed={isFilterOpen}
                title={labels.filters}
                className={cn(
                  "relative p-1.5 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-md transition-colors",
                  (isFilterOpen || activeFilterCount > 0) && "bg-secondary-100 text-secondary-700"
                )}
              >
                <Filter className="w-3.5 h-3.5" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-semibold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-1 rounded-md bg-secondary-100/70 p-0.5">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  title={labels.downloadCsv}
                  aria-label={labels.downloadCsv}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-secondary-600 hover:text-secondary-800 hover:bg-white/80 rounded transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-semibold tracking-wide">CSV</span>
                </button>
                <button
                  type="button"
                  onClick={handleExportXlsx}
                  title={labels.downloadXlsx}
                  aria-label={labels.downloadXlsx}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-secondary-600 hover:text-secondary-800 hover:bg-white/80 rounded transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-semibold tracking-wide">XLSX</span>
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
      )}
      <CardContent padding="none">
        {isFilterOpen && (
          <div className="border-b border-secondary-200 bg-secondary-50/70 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs font-semibold text-secondary-800">{labels.filters}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={addFilter}
                  className="flex items-center gap-1.5 rounded-md border border-secondary-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-secondary-700 hover:border-primary-200 hover:bg-primary-50/50 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {labels.addFilter}
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={filters.length === 0}
                  className={cn(
                    "rounded-md border border-secondary-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-secondary-600 transition-colors",
                    filters.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:border-secondary-300 hover:text-secondary-800"
                  )}
                >
                  {labels.clear}
                </button>
              </div>
            </div>

            {filters.length === 0 ? (
              <div className="mt-2 text-xs text-secondary-500">
                {labels.noFiltersYet}
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {filters.map((filter) => {
                  const type = columnTypes[filter.column] ?? 'string';
                  const options = operatorOptions[type] ?? operatorOptions.string;
                  const meta = getOperatorMeta(type, filter.operator) ?? options[0];
                  const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text';

                  return (
                    <div
                      key={filter.id}
                      className="grid grid-cols-1 items-center gap-2 md:grid-cols-[minmax(160px,1.2fr)_minmax(180px,1fr)_minmax(220px,1.4fr)_auto]"
                    >
                      <select
                        className="input h-9 px-3 py-2 text-xs"
                        value={filter.column}
                        onChange={(event) => {
                          const nextColumn = event.target.value;
                          const nextType = columnTypes[nextColumn] ?? 'string';
                          updateFilter(filter.id, {
                            column: nextColumn,
                            operator: getDefaultOperator(nextType),
                            value: '',
                            valueTo: '',
                          });
                        }}
                      >
                        {headers.map((header) => (
                          <option key={header} value={header}>
                            {header.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>

                      <select
                        className="input h-9 px-3 py-2 text-xs"
                        value={filter.operator}
                        onChange={(event) => {
                          const nextOperator = event.target.value as FilterOperator;
                          const nextMeta = getOperatorMeta(type, nextOperator);
                          updateFilter(filter.id, {
                            operator: nextOperator,
                            value: nextMeta?.needsValue ? filter.value : '',
                            valueTo: nextMeta?.needsSecondValue ? filter.valueTo : '',
                          });
                        }}
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      {meta.needsValue ? (
                        <div className="flex items-center gap-2">
                          <input
                            className="input h-9 px-3 py-2 text-xs"
                            type={inputType}
                            inputMode={type === 'number' ? 'decimal' : undefined}
                            placeholder={type === 'date' ? 'YYYY-MM-DD' : labels.value}
                            value={filter.value}
                            onChange={(event) =>
                              updateFilter(filter.id, { value: event.target.value })
                            }
                          />
                          {meta.needsSecondValue && (
                            <>
                              <span className="text-xs text-secondary-500">{labels.to}</span>
                              <input
                                className="input h-9 px-3 py-2 text-xs"
                                type={inputType}
                                inputMode={type === 'number' ? 'decimal' : undefined}
                                placeholder={type === 'date' ? 'YYYY-MM-DD' : labels.value}
                                value={filter.valueTo}
                                onChange={(event) =>
                                  updateFilter(filter.id, { valueTo: event.target.value })
                                }
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-secondary-500 italic">
                          {labels.noValueRequired}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeFilter(filter.id)}
                        aria-label={labels.removeFilter}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-secondary-200 bg-gradient-to-r from-secondary-50 to-secondary-100/50 backdrop-blur-sm">
                  {headers.map((header, index) => (
                    <th
                      key={header}
                      className={cn(
                        "px-4 py-2.5 text-left text-[11px] font-bold text-secondary-700 uppercase tracking-wider",
                        index === 0 && "rounded-tl-lg",
                        index === headers.length - 1 && "rounded-tr-lg"
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        {header.replace(/_/g, ' ')}
                        <div className="w-0.5 h-3 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full opacity-60"></div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={headers.length}
                      className="px-4 py-6 text-center text-xs text-secondary-500"
                    >
                      {labels.noResultsMatch}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, index) => (
                    <tr
                      key={index}
                      className={cn(
                        'hover:bg-gradient-to-r hover:from-primary-50/50 hover:to-secondary-50/50 transition-all duration-200',
                        index % 2 === 0 ? 'bg-white' : 'bg-secondary-25/50'
                      )}
                    >
                      {headers.map((header, cellIndex) => (
                        <td
                          key={header}
                          className="px-4 py-2.5 text-xs text-secondary-900 whitespace-nowrap font-medium"
                        >
                          <div
                            className={cn(
                              "flex items-center",
                              cellIndex === 0 && "font-semibold text-secondary-900"
                            )}
                          >
                            {row[header] !== null && row[header] !== undefined ? (
                              String(row[header])
                            ) : (
                              <span className="text-secondary-400 italic">-</span>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Table footer with summary */}
        <div className="border-t border-secondary-200 bg-gradient-to-r from-secondary-50/50 to-white px-4 py-2">
          <div className="flex items-center justify-between text-[11px] text-secondary-600">
            <span className="font-medium">
              {activeFilterCount > 0
                ? `${labels.showingOf} ${filteredData.length} / ${safeData.length} ${labels.results}`
                : `${labels.showingOf} ${safeData.length} ${safeData.length !== 1 ? labels.results : labels.result}`}
            </span>
            <span>
              {activeFilterCount > 0 ? `${labels.filtersShort}: ${activeFilterCount}` : labels.updatedJustNow}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
